// Pure helpers for recorded audio. No DOM or Web Audio here, so they can be
// unit tested in Node.

export interface StereoAudio {
  sampleRate: number;
  left: Float32Array<ArrayBuffer>;
  right: Float32Array<ArrayBuffer>;
}

export const durationOf = (a: StereoAudio): number => a.left.length / a.sampleRate;

export function concat(chunks: Float32Array[]): Float32Array<ArrayBuffer> {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Float32Array(total);
  let o = 0;
  for (const c of chunks) {
    out.set(c, o);
    o += c.length;
  }
  return out;
}

export function peakOf(...channels: Float32Array[]): number {
  let p = 0;
  for (const ch of channels) for (let i = 0; i < ch.length; i++) p = Math.max(p, Math.abs(ch[i]));
  return p;
}

/**
 * Make a take DAW-ready: drop the silence before the first note (keeping a
 * few milliseconds so the attack isn't clipped) and fade the very end so it
 * never stops on a click.
 */
export function tidy(a: StereoAudio, threshold = 0.0015, preRollMs = 8, fadeMs = 40): StereoAudio {
  const n = a.left.length;
  let start = 0;
  while (start < n && Math.abs(a.left[start]) < threshold && Math.abs(a.right[start]) < threshold) start++;
  if (start === n) return a;
  start = Math.max(0, start - Math.floor((preRollMs / 1000) * a.sampleRate));
  const left = a.left.slice(start);
  const right = a.right.slice(start);
  const fade = Math.min(left.length, Math.floor((fadeMs / 1000) * a.sampleRate));
  for (let i = 0; i < fade; i++) {
    const g = i / fade;
    left[left.length - 1 - i] *= g;
    right[right.length - 1 - i] *= g;
  }
  return { sampleRate: a.sampleRate, left, right };
}

/** Min/max pairs per bucket, for drawing a waveform. */
export function peaks(a: StereoAudio, buckets: number): Float32Array {
  const out = new Float32Array(buckets * 2);
  const n = a.left.length;
  for (let b = 0; b < buckets; b++) {
    const s = Math.floor((b / buckets) * n);
    const e = Math.max(s + 1, Math.floor(((b + 1) / buckets) * n));
    let lo = 0;
    let hi = 0;
    for (let i = s; i < e && i < n; i++) {
      const v = (a.left[i] + a.right[i]) * 0.5;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    out[b * 2] = lo;
    out[b * 2 + 1] = hi;
  }
  return out;
}

/** Interleaved PCM WAV. 24-bit by default, the studio standard. */
export function encodeWav(a: StereoAudio, bitDepth: 16 | 24 = 24): ArrayBuffer {
  const channels = 2;
  const bytes = bitDepth / 8;
  const frames = a.left.length;
  const dataSize = frames * channels * bytes;
  const buf = new ArrayBuffer(44 + dataSize);
  const v = new DataView(buf);
  const str = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i));
  };
  str(0, 'RIFF');
  v.setUint32(4, 36 + dataSize, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, channels, true);
  v.setUint32(24, a.sampleRate, true);
  v.setUint32(28, a.sampleRate * channels * bytes, true);
  v.setUint16(32, channels * bytes, true);
  v.setUint16(34, bitDepth, true);
  str(36, 'data');
  v.setUint32(40, dataSize, true);
  let o = 44;
  const max = bitDepth === 16 ? 0x7fff : 0x7fffff;
  for (let i = 0; i < frames; i++) {
    for (const ch of [a.left, a.right]) {
      const s = Math.max(-1, Math.min(1, ch[i]));
      const q = Math.round(s * max);
      if (bitDepth === 16) {
        v.setInt16(o, q, true);
      } else {
        v.setUint8(o, q & 0xff);
        v.setUint8(o + 1, (q >> 8) & 0xff);
        v.setUint8(o + 2, (q >> 16) & 0xff);
      }
      o += bytes;
    }
  }
  return buf;
}

export function toInt16(ch: Float32Array): Int16Array {
  const out = new Int16Array(ch.length);
  for (let i = 0; i < ch.length; i++) {
    const s = Math.max(-1, Math.min(1, ch[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

export function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function fileStamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

export function formatTime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
